// apps/share-web/src/text-share/words-view.ts
//
// Страница текстовой ссылки: имя сетлиста, песни по порядку, чтение поверх.
//
// «Кину в чат слова к воскресенью» — ссылка открывается без логина и без аудио.
// Поэтому здесь НЕТ микшера, дорожек, транспорта и входа: страница собрана из
// другого набора, а не из плеера со спрятанными частями.
//
// ЧИТАЕТ она тем же слоем, что и аудио-шейр — `chart-fullscreen.ts` волны A, —
// и это не переиспользование ради экономии. Слой уже не знает про аудио: он
// принимает text, sections, position и setlist, а играть умеет только через
// НЕОБЯЗАТЕЛЬНЫЙ шов транспорта. Здесь этого шва нет вовсе, и ряд Play в слое не
// появляется: кнопка, которая ничего не делает, хуже отсутствующей. Заперто
// тестом «кнопки Play в DOM НЕТ» — и на этой странице, и в самом слое.
//
// НАВИГАЦИЯ ПОЗИЦИОННАЯ. У текстовой ссылки нет отдельных шейров на песню:
// песни лежат массивом в одной нагрузке, и «следующая» — это индекс. Слой
// поэтому и отдаёт наружу место соседа (см. `SetlistEntry` там же), а страница
// открывает его сама, не уводя браузер никуда: адрес ссылки один на весь
// сетлист.
//
// РЕЖИМ ТЕЛЕФОНА — тот же `watchPhoneMode`, что у блока чарта в плеере, а не
// третий способ спросить окно. Панели здесь нет вовсе, поэтому режим решает
// ровно одно: потолок колонок (2 против 3 — три на телефоне не читаются). И
// считается он ЖИВЬЁМ: окно, сужённое посреди чтения, обязано пересчитать
// потолок, иначе дропдаун показывает две колонки, а лист стоит в трёх (тот же
// долг, что закрыт в плеере — TD-260828-chart-phone-mode-snapshotted-at-mount).
//
// ЛАЙВ (волна C) — НАДСТРОЙКА, а не режим страницы. Источник позиции приезжает
// швом `live`, и без него страница ведёт себя ровно как в волне B: кнопки нет,
// импульса нет, лист стоит. Со швом добавляются три вещи и ни одной больше —
// кнопка состояния (`live-badge.ts`), решение «подключаться и куда переходить»
// (`live-follow.ts`) и пробуждение экрана (`wake-lock.ts`). Здесь остаётся
// проводка: правило роста волны — «новая фича = новые модули + тонкая
// оркестрация», а этот файл и без лайва не маленький.
//
// ТРАНСПОРТА ЛАЙВ НЕ ПРИНОСИТ. Соблазн есть — раз лист поехал, кажется, что
// рядом просится Play, — но играть здесь по-прежнему нечем: аудио на этой
// поверхности нет вовсе, а лидер и так звучит в зале. Замок волны B («кнопки
// Play в DOM НЕТ») продолжает держать и при живом лайве, отдельным тестом.

// Лист и слой — их таблица стилей. Модуль тянет её САМ: страница слов не
// монтирует player-view, а chart.css приезжала именно оттуда (mount.ts), и
// «кто-то выше уже импортировал» здесь не гарантия, а совпадение.
import '../styles/chart.css';
import '../styles/text-share.css';

import type { TextSharePayload } from '@bandway/share-types';
import {
  mountChartFullscreen,
  type ChartFullscreenHandle,
  type ChartFullscreenDeps,
} from '../player-view/chart-fullscreen.js';
import { createFullscreenSlot, type FullscreenSlot } from '../player-view/fullscreen-slot.js';
import { watchPhoneMode, type PhoneModeWatch } from '../player-view/phone-mode.js';
import type { PositionSource } from '../chart/chart-follow.js';
import type { LinkState } from '../live/live-model.js';
import { createLiveBadge } from './live-badge.js';
import { liveStep } from './live-follow.js';
import { createWakeLock, type WakeLockSeam } from './wake-lock.js';
import { wordsSongs, wordsTitle, type WordsSong } from './words-model.js';
import { loadReaderPrefs, saveReaderPrefs, type PrefsStore, type ReaderTheme } from '../chart/reader-prefs.js';
import { createLocalClock, fmtClock } from '../chart/local-clock.js';
import { setAppTheme } from '../theme-boot.js';
import { sectionIndexAt } from '../chart/chart-follow.js';
import '../styles/theme-light.css';
import { renderWordsEmpty } from './words-status.js';
import { t } from '../ui/i18n.js';

export interface WordsViewDeps {
  payload: TextSharePayload;
  /**
   * Наблюдатель за режимом телефона. В проде — `watchPhoneMode`; подменяется в
   * тестах, и не ради изоляции: happy-dom не объявляет переход «телефон →
   * широкий» вовсе (его MediaQueryList заводит `matchesState = false` при
   * подписке — замерено, phone-mode.ts), так что живой пересчёт иначе
   * недоказуем.
   */
  watchPhone?: (onChange: (phone: boolean) => void) => PhoneModeWatch;
  /** Слот слоя в body. Шов для тестов; в проде — `createFullscreenSlot`. */
  createSlot?: () => FullscreenSlot;
  /**
   * Шов ведения — по прецеденту панели и полного экрана (`chart-panel.ts`,
   * `chart-fullscreen.ts`): в проде остаётся настоящий `createChartFollow`
   * слоя (страница его не выбирает сама, шов прокидывается ему), подменяется
   * только в тестах, и не ради изоляции — у настоящего ведения первый замок
   * «нет ширины — не считаем», а happy-dom раскладку не считает вовсе, так что
   * лид иначе недоказуем.
   */
  createFollow?: ChartFullscreenDeps['createFollow'];
  /**
   * Лайв-сессия. Поля нет — страница ведёт себя ровно как в волне B: кнопки
   * Live нет вовсе, лист стоит, импульс не заводится. Это не вырожденный
   * случай, а честный: пока лидер не ведёт, вести нечего.
   *
   * Клиента создаёт ТОТ, КТО ЗНАЕТ АДРЕС ССЫЛКИ (main.ts), он же его и
   * останавливает: страница им пользуется и не владеет. Иначе повторная попытка
   * загрузки нагрузки заводила бы второй цикл опроса рядом с первым — и каждый
   * читатель в зале платил бы за это удвоенной частотой запросов.
   */
  live?: WordsLiveSource;
  /**
   * Импульс страницы: им тикают кнопка лайва и переход по песням за лидером.
   * В тестах подменяется — ждать настоящих секунд нечем.
   */
  pulse?: (tick: () => void) => PagePulse;
  /** Пробуждение экрана. В проде — `createWakeLock()`; в тестах — с подставным API. */
  wakeLock?: WakeLockSeam;
  /**
   * Хранилище настроек читателя (reader-prefs). Не задано — localStorage;
   * `null` — не хранить (тесты, приватный режим без хранилища).
   */
  prefsStore?: PrefsStore | null;
  /**
   * Часы страницы. Ими живёт ровно одно решение — пауза возврата из общего
   * списка (`LIST_GRACE_MS` в live-follow.ts), и подменяются они в тестах по той
   * же причине, что часы в лайв-клиенте: ждать настоящих десяти секунд нечем.
   */
  now?: () => number;
}

/**
 * Клиент лайва глазами страницы: позиция, состояние связи и внеплановый опрос.
 * Структурный тип, а не импорт `LiveClient`, — странице не нужен ни его
 * `stop()`, ни знание о том, что позиция берётся из сети.
 */
export interface WordsLiveSource {
  position: PositionSource;
  state(): LinkState;
  poll(): void | Promise<void>;
}

export interface PagePulse {
  stop(): void;
}

export interface WordsViewHandle {
  unmount(): void;
  /**
   * Заменить содержимое страницы, НЕ пересобирая её (спека §4.4-4.6).
   *
   * Перемонтаж был бы проще и неверен: он сбросил бы открытый лист, прокрутку и
   * настройки читателя — то есть выдернул бы страницу из-под человека ровно
   * тогда, когда ведущий правит слова на ходу.
   */
  setPayload(next: TextSharePayload): void;
}

/**
 * Период импульса страницы.
 *
 * Это НЕ кадр. Кадр держит слой чтения (chart-fullscreen.ts) и тикает им ведение
 * листа и шапку — там счёт идёт на пиксели прокрутки. Здесь счёт идёт на песни:
 * якорь приезжает раз в 10 с, вид кнопки меняется на масштабе десятков секунд, и
 * шестьдесят проверок в секунду были бы шестьюдесятью проверками впустую на
 * телефоне, лежащем на стойке весь час служения. Полсекунды — потолок задержки
 * перехода на песню лидера, и он вчетверо меньше периода самого опроса.
 */
const PULSE_MS = 500;

const defaultPulse = (tick: () => void): PagePulse => {
  const id = setInterval(tick, PULSE_MS);
  return { stop: () => { clearInterval(id); } };
};

const el = (tag: string, cls?: string, txt?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
};

/**
 * `noindex` МЕТОЙ, а не только заголовком воркера.
 *
 * Приватность спеки: «страница не находится поиском». Заголовок
 * `X-Robots-Tag` стоит на ответе API и на `/t/*` в `_headers`, но метой это
 * работает и там, где заголовков нет вовсе — локальный dev, preview, и любой
 * будущий хостинг страницы. Ставится ТОЛЬКО если своей ещё нет, и снимается
 * ровно та, что поставили: чужую метку (например будущую глобальную в
 * index.html) стирать нельзя.
 */
function claimNoindex(): () => void {
  const head = document.head;
  const existing = [...head.querySelectorAll('meta')].find((m) => m.getAttribute('name') === 'robots');
  if (existing) return () => {};
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'robots');
  meta.setAttribute('content', 'noindex, nofollow');
  head.appendChild(meta);
  return () => { meta.remove(); };
}

/** Одна строка списка: номер, имя, мета (или пометка «слов нет»). */
function songRow(song: WordsSong, onOpen: (index: number) => void): HTMLElement {
  const item = el('li', 'ts-item');
  // Строка — обычная <button>, а не `<bw-button>`: это РЯД во весь список, как
  // `.lib-setlist` в библиотеке (ui/library-view.ts), а не кап дизайн-системы.
  // Кнопкой она при этом остаётся настоящей — фокус, Enter и объявление
  // «кнопка» приходят от элемента, а не от нашего обработчика на div.
  const btn = document.createElement('button');
  btn.className = 'ts-song';
  btn.dataset.index = String(song.index);

  btn.appendChild(el('span', 'ts-song-n', String(song.index + 1)));
  const main = el('span', 'ts-song-main');
  main.appendChild(el('span', 'ts-song-name', song.name));
  // Пустая мета — дыра в строке, а не строка. Песня без слов вместо неё несёт
  // пометку: читатель должен знать это ДО того, как откроет пустой лист.
  if (!song.hasWords) main.appendChild(el('span', 'ts-song-flag', t('textShare.noWords')));
  else if (song.meta !== '') main.appendChild(el('span', 'ts-song-meta', song.meta));
  btn.appendChild(main);

  btn.addEventListener('click', () => onOpen(song.index));
  item.appendChild(btn);
  return item;
}

export function mountWordsView(host: HTMLElement, deps: WordsViewDeps): WordsViewHandle {
  // `let`, а не `const`: содержимое ссылки теперь изменяемо (спека §4.1), и
  // страница обязана уметь принять новое, оставшись на месте.
  let songs = wordsSongs(deps.payload);
  const dropNoindex = claimNoindex();

  const page = el('div', 'ts-page');
  const head = el('header', 'ts-head');
  head.appendChild(el('p', 'ts-eyebrow', t('textShare.wordsOnly')));
  const title = document.createElement('h1');
  title.className = 'ts-title';
  title.textContent = wordsTitle(deps.payload);
  head.appendChild(title);
  const countEl = el('p', 'ts-count', songs.length === 1
    ? t('textShare.songCountOne')
    : t('textShare.songCount', { n: songs.length }));
  head.appendChild(countEl);
  const live = deps.live ?? null;
  // Кнопки нет ВОВСЕ там, где нет источника: пустая плашка «лайва не будет»
  // ничего читателю не сообщает, а место в шапке телефона занимает.
  const badge = live ? createLiveBadge(() => toggleLive()) : null;
  if (badge) head.appendChild(badge.el);
  page.appendChild(head);

  // ── чтение ────────────────────────────────────────────────────────────────
  // Слой стоит в body, а не внутри страницы: «полный экран должен быть полным
  // экраном» (пункт приёмки A1-1, причина — в шапке fullscreen-slot.ts). Он же
  // убирает список под слоем из обхода Tab и из дерева доступности.
  const slot = (deps.createSlot ?? createFullscreenSlot)();
  let layer: ChartFullscreenHandle | null = null;
  /** Какую песню читают. −1 — слой закрыт. */
  let reading = -1;
  /**
   * Колонки принадлежат ЧИТАТЕЛЮ и переживают смену песни: это привычка глаз, а
   * не свойство песни. Сдвиг тональности — наоборот, свойство ПЕСНИ, поэтому у
   * него есть владелец: перешли к соседу — сдвиг сброшен в ноль (чужая песня —
   * своя тональность, то же правило, что у лайва в спеке). А «✕ и снова
   * открыть» ту же песню сдвиг сохраняет: слой пересоздаётся на каждое
   * открытие, и без внешнего владельца транспозицию приходилось бы ставить
   * заново после каждого касания крестика — замер ревью волны A.
   */
  // Words page v2: настройки ЧИТАТЕЛЯ читаются один раз при монтаже и живут на
  // этом устройстве (reader-prefs). Это и есть фикс «зум не сохраняется»
  // (14.09): слой умел принимать шаг размера, а страница его не передавала —
  // каждое открытие песни ставило базу.
  const store = deps.prefsStore === undefined ? undefined : deps.prefsStore;
  const prefs = loadReaderPrefs(store);
  let columns = prefs.columns;
  let fontPct = prefs.fontPct;
  let theme: ReaderTheme = prefs.theme;
  let fit = prefs.fit;
  let follow = prefs.follow;
  let showChords = prefs.showChords;
  let showLyrics = prefs.showLyrics;
  const remember = (patch: Parameters<typeof saveReaderPrefs>[0]): void => { saveReaderPrefs(patch, store); };
  // Стамп темы — у общего владельца (theme-boot.ts), см. song-words-view.ts.
  setAppTheme(theme);
  const setTheme = (v: ReaderTheme): void => { theme = v; setAppTheme(v); remember({ theme: v }); };
  /** Локальные часы «ведения по темпу» — одна пара на страницу, заводится на песню. */
  const clock = createLocalClock({ now: () => nowMs() });
  const clockAvailable = (index: number): boolean => {
    const s = songs[index];
    return !!s && s.bpm > 0 && s.sections.some((x) => (x.name ?? '').trim() !== '');
  };
  let shift = 0;
  let shiftOwner = -1;
  /**
   * Ведётся ли лист, КОТОРЫЙ СЕЙЧАС СТОИТ. Не «ведём ли мы вообще» — именно
   * свойство смонтированного слоя: расписание он замораживает при монтаже, а
   * ответ `followingSong` меняется под ним от чужих событий (подключились,
   * отключились, лидер дошёл до песни, которую читатель открыл заранее).
   * Расхождение этих двух значений — единственная причина пересобрать слой на
   * той же песне, и она же гарантия, что просто так он не пересобирается.
   */
  let layerFollows = false;

  const wake = deps.wakeLock ?? createWakeLock();
  /** Ведём ли лист за лидером. Решает и подсветку кнопки, и позицию слоя. */
  let connected = false;
  /** Читатель отключился рукой. ЛИПКО — обратно только кликом (live-follow.ts). */
  let manualOff = false;
  /**
   * Песня, на которую страница уже переходила по воле лидера. −1 — ни разу.
   * Нужна только для ЗАКРЫТОГО листа — причина в `lastLedIdx` (live-follow.ts).
   */
  let lastLedIdx = -1;
  /**
   * Когда читатель закрыл лист и ушёл в общий список. От этого момента, а НЕ от
   * подключения, отсчитывается пауза возврата (`LIST_GRACE_MS`): закрыл — 10 с —
   * вернуло; закрыл снова — снова 10 с. Пишется в одном месте (`closeLayer`),
   * и это не экономия: `reading = -1` тоже ставится только там, значит «лист
   * закрыт» и «когда закрыт» не могут разъехаться.
   */
  let closedAt: number | null = null;
  const nowMs = deps.now ?? (() => Date.now());

  /** Ряды списка по порядку песен — их помечает `markNowPlaying`. */
  const rowButtons: HTMLButtonElement[] = [];
  /**
   * Пометка «играет сейчас» — ОДИН узел на страницу, переезжающий между рядами.
   *
   * Тот же приём, что у кнопки Live (узел один, владеет им страница): двадцать
   * спрятанных пометок — двадцать узлов ради одного видимого.
   *
   * ⚠️ ПОЧЕМУ НЕ `bw-tag`. Он был первым выбором — на десктопе «now» в сетлисте
   * помечен именно им (`bw-setlist-card[status='now']`), — и на телефоне это
   * читалось неверно, замерено живьём: `variant="filled"` красит кап в
   * `--bw-tag-bg: var(--fg-3)`, то есть светло-серой плашкой с тёмным текстом, и
   * в списке на 375 px она громче имён песен. Тона `green` у `bw-tag` нет вовсе
   * (`default | amber | red`), а красить ДС-компонент из потребителя запрещено —
   * ровно тот довод, по которому и сама кнопка Live на этой странице не `bw-tag`
   * (шапка `.ts-live` в text-share.css). Поэтому пометка говорит ЯЗЫКОМ ЭТОЙ
   * СТРАНИЦЫ: зелёный лайва, тот же `--green`, что у точки в кнопке над списком.
   * Новой сущности при этом не появилось — состояние ряда и подпись к нему.
   *
   * Нет лайва — нет и узла: волна B про играющую песню не знает ничего.
   */
  const nowTag = live
    ? el('span', 'ts-song-now', t('textShare.nowPlaying'))
    : null;

  /**
   * Какая песня звучит у лидера — глазами читателя, стоящего в общем списке.
   *
   * Пока лист закрыт, на экране нет ни следа лайва: возврат через паузу без
   * такого следа выглядит как случайный прыжок, а Сергей просил вернуть «к
   * секции И ПЕСНЕ нужной» — то есть песню надо ещё и НАЗВАТЬ, пока он выбирает.
   * Состояние ряда — словарём десктопа (`is-current` в left-rail.js,
   * `bw-setlist-card[status='now']`), а не третьим способом сказать «сейчас».
   *
   * −1 — не помечен никто: лайва нет, или читатель отключился рукой. Во втором
   * случае возврата не будет вовсе, и пометка обещала бы движение, которого не
   * произойдёт.
   */
  const markNowPlaying = (index: number): void => {
    for (let i = 0; i < rowButtons.length; i++) {
      const row = rowButtons[i];
      const on = i === index;
      row.classList.toggle('is-now', on);
      // Ряд «текущий в наборе» — стандартное объявление для скринридера; текст
      // самой пометки он прочитает вместе с именем песни.
      if (on) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    }
    const row = index >= 0 ? rowButtons[index] : undefined;
    // appendChild ПЕРЕНОСИТ узел: снимать его с прежнего ряда отдельной строкой
    // не нужно, а вот убрать совсем — нужно.
    if (row && nowTag) row.appendChild(nowTag);
    else nowTag?.remove();
  };

  /**
   * Позиция для слоя чтения — ОДИН объект на всю жизнь страницы, решающий
   * заново на каждый кадр.
   *
   * Слой замораживает `position` при монтаже, а подключение и отключение
   * случаются посреди чтения. Отдать сюда `live.position` напрямую значило бы,
   * что «отключиться» не отключает ничего: лист продолжал бы ехать за лидером,
   * пока читатель не закроет слой. Отсутствие источника (волна B, аудио-шейр)
   * выражается тем же `null` — и ведение с шапкой «сейчас → дальше» молчат.
   *
   * ⚠️ И ПОЗИЦИЯ ОТДАЁТСЯ ТОЛЬКО ПРО ОТКРЫТУЮ ПЕСНЮ. Секунды якоря сами по себе
   * ничего не значат: они отсчитаны по расписанию ПЕСНИ ЛИДЕРА, а слой
   * смонтирован с расписанием той песни, которую читатель открыл. Без этой
   * проверки лист чужой песни поехал бы по чужому расписанию: подсветка встаёт
   * на случайную секцию и утаскивает лист за собой.
   *
   * С правилом 3 (`live-follow.ts`: подключён — значит следуешь) чужая песня у
   * подключённого читателя живёт не дольше одного импульса, но проверка от этого
   * не стала лишней: полсекунды катающегося самого по себе листа — это ровно то,
   * что видно глазом, а у отключённой страницы и у закрытого крестиком листа
   * чужая песня живёт сколько угодно.
   *
   * Фильтр стоит ЗДЕСЬ, а не в chart-follow.ts: `songIdx` — понятие страницы
   * («какая из песен нагрузки»), общий слой ведения о существовании соседей не
   * знает вовсе. Побочно тем же сравнением отсекается `songIdx` вне диапазона:
   * `reading` — всегда индекс существующей песни.
   */
  const position: PositionSource = {
    now: () => {
      if (followingSong(reading)) return live!.position.now();
      // Ведение по темпу: часы этой песни, когда лайв её не ведёт.
      if (followingClock(reading)) return clock.now();
      return null;
    },
  };

  /** Лист этой песни идёт по локальным часам: режим включён, лайв её не ведёт, у песни есть темп и секции. */
  function followingClock(index: number): boolean {
    return follow && index >= 0 && !followingSong(index) && clockAvailable(index) && clock.songIdx() === index;
  }

  /**
   * Ведём ли лист ЭТОЙ песни прямо сейчас: страница подключена И лидер стоит
   * именно на ней.
   *
   * ОДИН предикат на два ответа — и позицию слою, и расписание при монтаже, —
   * потому что расходиться им нельзя ни в одну сторону: расписание без позиции
   * даёт ряд из двух черт («дыра в шапке, а не сведения», chart-now-next.ts), а
   * позиция без расписания — тихая ложь про чужие часы. Второй копией правила
   * они разъехались бы на первой же правке.
   *
   * `reading === -1` (слой закрыт) отвечает `false` сам: индекса −1 у лидера не
   * бывает.
   */
  function followingSong(index: number): boolean {
    return connected && !!live && (live.position.now()?.songIdx ?? null) === index;
  }

  /**
   * «Хотим держать экран» — просим замок пробуждения.
   *
   * Зовётся на КАЖДОМ касании (строка списка, стрелки слоя, кнопка Live) и на
   * автоподключении тоже. Автоподключение — не исключение, а главный сценарий
   * волны: музыкант открыл ссылку до служения, положил телефон на стойку, и
   * слой встал сам. Не попросив там, мы обещали бы «экран не гаснет» ровно тем,
   * кто ничего не нажимал, — и они возвращались бы к чёрному телефону через
   * полминуты.
   *
   * Отказ без жеста ожидаем и уже глотается молча (wake-lock.ts, пункт 3), то
   * есть попытка не стоит ничего; а на телефоне, которого касались раньше
   * (открыл ссылку, полистал список), она срабатывает.
   */
  const gesture = (): void => { wake.request(); };

  const dropLayer = (): void => {
    layer?.destroy();
    layer = null;
    // Кнопка Live уезжала в первый ряд слоя (слот `badge` там же). Слой снят —
    // её место снова в шапке страницы: узел один, и владеет им страница.
    if (badge) head.appendChild(badge.el);
  };

  const closeLayer = (): void => {
    dropLayer();
    reading = -1;
    // Момент ухода в список. Читатель мог закрыть лист и не будучи подключённым
    // — метка от этого не портится: её вообще не спрашивают, пока мы не следуем.
    closedAt = nowMs();
    slot.uncover();
  };

  const openAt = (index: number): void => {
    const song = songs[index];
    // Мусорный индекс не открывает ничего и не роняет страницу: он приходит и
    // от кнопок слоя, и из будущего лайва.
    if (!song) return;
    if (index !== shiftOwner) { shift = 0; shiftOwner = index; }
    // Часы заводятся на песню: другая песня — позиция 0, пауза.
    if (clock.songIdx() !== index) clock.arm(index, song.durationSec);
    // Старый слой снимается ДО нового: два слоя друг на друге это два кадра,
    // два слушателя Escape и лист, который читатель не открывал.
    dropLayer();
    reading = index;
    slot.cover();
    // Один ответ на весь монтаж: расписание слою и память о том, с каким
    // расписанием он смонтирован, обязаны быть ОДНИМ решением. Двумя вызовами
    // они разъехались бы на первой же правке `followingSong`.
    layerFollows = followingSong(index);
    const hasClock = !layerFollows && follow && clockAvailable(index);
    layer = mountChartFullscreen(slot.el, {
      text: song.text,
      // Связи — вместе с текстом и по той же причине: без них лист этой
      // поверхности не следил бы за русским чартом вовсе.
      links: song.links,
      songName: song.name,
      songKey: song.key,
      songMeta: song.meta,
      // Числа темпа — АРИФМЕТИКЕ лида (слой сам зовёт leadSeconds на них),
      // songMeta выше — уже готовая строка ЧЕЛОВЕКУ: одни и те же данные едут
      // сюда двумя видами по разным адресам, а не один вместо другого.
      bpm: song.bpm,
      timeSignature: song.timeSignature,
      // РАСПИСАНИЕ ЕДЕТ СЛОЮ РОВНО ТОГДА, когда лист этой песни ведётся, и это
      // замер, а не осторожность. Ряд «сейчас → дальше» рисуется, когда
      // секции ЕСТЬ, а заполняется от ПОЗИЦИИ — и без неё встаёт двумя чертами
      // на всё чтение. Замерено живьём на девфикстуре волны B: `.cw-fs-row3` —
      // 28,4 px высоты, содержимое «——». Правило волны A про тот же ряд
      // дословно: «две черты подряд это дыра в шапке, а не сведения»
      // (chart-now-next.ts). Отключённая страница позиции не отдаёт (см.
      // `position` выше), значит расписание дало бы ей ровно эту дыру.
      //
      // И та же дыра — у читателя, ушедшего листать соседнюю песню: позиция
      // якоря отсчитана по расписанию ПЕСНИ ЛИДЕРА, для открытой она не значит
      // ничего, и `position` про неё честно молчит. Вопрос поэтому один на оба
      // случая — `followingSong`.
      //
      // Расписание едет ПОЛНЫМ — с безымянными секциями тоже (words-model.ts,
      // `sections`): позиция принадлежит всей аранжировке, а имена нужны только
      // для сопоставления секции с блоком чарта.
      // Расписание едет слою и для ведения по темпу: тогда лента и подсветка
      // идут по локальным часам, как при лайве — только без лидера.
      sections: layerFollows || hasClock ? song.sections : [],
      // Слою нужны только ИМЕНА соседей: чем открыть песню, он не знает и знать
      // не должен — переход возвращается сюда местом.
      setlist: songs.map((s) => ({ songName: s.name })),
      currentIndex: index,
      isPhone: watch.isPhone(),
      position,
      // Кнопка Live переезжает в первый ряд слоя: он накрывает страницу
      // целиком, и оставленная под ним кнопка для читателя не существует.
      badge: badge?.el,
      initialShift: shift,
      initialColumns: columns,
      initialFontPct: fontPct,
      theme, fit, follow, showChords, showLyrics,
      followAvailable: clockAvailable(index),
      onShiftChange: (n) => { shift = n; },
      onColumnsChange: (n) => { columns = n; remember({ columns: n }); },
      onFontPctChange: (n) => { fontPct = n; remember({ fontPct: n }); },
      onThemeChange: (v) => setTheme(v),
      onFitChange: (on) => { fit = on; remember({ fit: on }); },
      onFollowChange: (on) => {
        follow = on; remember({ follow: on });
        if (!on) clock.pause();
        // Пилюля и расписание монтируются вместе со слоем — пересобрать на той же песне.
        if (clockAvailable(index) && !layerFollows) openAt(index);
      },
      onShowChordsChange: (on) => { showChords = on; remember({ showChords: on }); },
      onShowLyricsChange: (on) => { showLyrics = on; remember({ showLyrics: on }); },
      // Тап по чипу при ведении по темпу — часы с начала секции.
      onJumpSection: (i) => { if (hasClock) { const sec = song.sections[i]; if (sec) clock.seek(sec.startSec); } },
      followPill: hasClock ? {
        isPlaying: () => clock.isPlaying(),
        toggle: () => { gesture(); if (clock.isPlaying()) clock.pause(); else clock.start(); },
        label: () => {
          const pos = clock.posSec();
          const si = sectionIndexAt(song.sections, pos);
          const name = si >= 0 ? (song.sections[si].name ?? '').trim() : '';
          return name ? `${fmtClock(pos)} · ${name}` : fmtClock(pos);
        },
      } : undefined,
      onNavigate: (at) => { gesture(); openAt(at); },
      onClose: () => closeLayer(),
      createFollow: deps.createFollow,
      // transport НЕ передаётся: играть нечем. См. шапку модуля.
    });
  };

  // Наблюдатель создаётся ПОСЛЕ openAt, хотя openAt его читает: тело стрелки
  // читает переменную в момент ВЫЗОВА, а первый вызов приходит либо из клика по
  // строке, либо из этого самого колбэка — то есть всегда позже. Обратный
  // порядок невозможен: колбэку нужен openAt. Та же пара, что в mount.ts, и с
  // той же оговоркой — одна переставленная строка превращает это в
  // ReferenceError, поэтому порядок объявлен намеренно, а не сложился.
  const watch = (deps.watchPhone ?? watchPhoneMode)((phone) => {
    void phone;
    // Порог перешли посреди чтения — потолок колонок стал другим, и слой
    // обязан пересобраться на ТОЙ ЖЕ песне с теми же настройками читателя.
    // Список песен от режима не зависит вовсе, поэтому вне чтения делать
    // нечего.
    if (reading >= 0) openAt(reading);
  });

  // Держатель списка живёт отдельно от его содержимого: перерисовка меняет
  // ДЕТЕЙ, а сам узел остаётся на месте в странице. Замена узла целиком стоила
  // бы прокрутки — а список это то, что читатель в этот момент читает.
  const listHost = el('div', 'ts-list-host');
  page.appendChild(listHost);

  const renderList = (): void => {
    listHost.textContent = '';
    rowButtons.length = 0;
    if (songs.length === 0) {
      // Нагрузка без песен — не ошибка загрузки: ссылка живая, читать нечего.
      const box = el('div', 'ts-empty');
      renderWordsEmpty(box);
      listHost.appendChild(box);
      return;
    }
    const list = el('ol', 'ts-list');
    for (const song of songs) {
      const item = songRow(song, (index) => { gesture(); openAt(index); });
      // Ряд запоминается, чтобы пометку «играет сейчас» можно было переставлять
      // на импульсе, а не перерисовывать список: список — это то, что читатель
      // в этот момент читает, и пересборка под пальцем сбрасывала бы прокрутку.
      const btn = item.querySelector('button');
      if (btn) rowButtons.push(btn);
      list.appendChild(item);
    }
    listHost.appendChild(list);
  };
  renderList();

  /**
   * Один импульс: где лидер, что с кнопкой, не пора ли перейти на другую песню.
   *
   * Решение — в `live-follow.ts`, здесь только исполнение. Позиция для решения
   * берётся у клиента НАПРЯМУЮ, а не через `position`: тот уже отфильтрован
   * подключением, и по нему отключённая страница никогда не узнала бы, что
   * лидер вернулся.
   */
  const syncLive = (): void => {
    if (!live || !badge) return;
    const state = live.state();
    const at = live.position.now();
    const step = liveStep({
      state,
      ledSongIdx: at?.songIdx ?? null,
      connected,
      manualOff,
      readingIdx: reading,
      lastLedIdx,
      closedAtMs: closedAt,
      nowMs: nowMs(),
    });
    if (step.connect) {
      connected = true;
      // Подключились без единого касания — экран всё равно обязан не гаснуть.
      gesture();
    }
    // Лайв ведёт открытую песню — локальные часы уступают ему.
    if (connected && clock.isPlaying() && followingSong(reading)) clock.pause();
    if (step.openAt !== null) {
      // Ход лидера — и когда он сменил песню, и когда он ВОЗВРАЩАЕТ читателя на
      // свою (правило 3). Оба случая одинаково снимают у закрытого листа право
      // стоять в списке: закрыл ✕ на песне лидера — стоишь, пока он не двинулся.
      lastLedIdx = step.openAt;
      openAt(step.openAt);
    } else if (reading >= 0 && followingSong(reading) !== layerFollows) {
      // Слой замораживает расписание при монтаже, а право на расписание меняется
      // под ним. Два случая, и оба замерены:
      //  • подключились обратно на той же песне — без пересборки лист стоял бы;
      //  • читатель заглянул вперёд, и ЛИДЕР ДОШЁЛ до этой песни — слой был
      //    смонтирован без расписания (чужие часы), и без пересборки лист так и
      //    не поехал бы, хотя теперь ведётся честно. Это дефект моей же правки
      //    правила 3: возврат к лидеру ничего не пересобирает, когда песня и так
      //    правильная, — а расписание при этом появилось.
      openAt(reading);
    }
    badge.sync(state, connected);
    // Пометка ставится ПОСЛЕ решения о подключении: `connected` мог измениться
    // этим же импульсом, и пометка обязана говорить о том же, что кнопка.
    markNowPlaying(connected ? at?.songIdx ?? -1 : -1);
  };

  /** Кнопка Live: одно действие в обе стороны. */
  const toggleLive = (): void => {
    if (!live || !badge) return;
    gesture();
    if (connected) {
      connected = false;
      manualOff = true;
      if (reading >= 0) openAt(reading);
      badge.sync(live.state(), connected);
      // Ранний выход мимо `syncLive` — пометку снимаем здесь же: отключившемуся
      // рукой возврата не будет, и «играет сейчас» обещало бы движение.
      markNowPlaying(-1);
      return;
    }
    // Явное решение снимается явным решением — и только им.
    manualOff = false;
    // И паузу возврата из списка этот же жест отменяет: человек нажал «веди
    // меня», ждать после такого нечего (`closedAtMs` в live-follow.ts).
    closedAt = null;
    syncLive();
  };

  /**
   * Возврат вкладки из фона.
   *
   * Две вещи сразу, и обе замерены соседями: браузер отпускает замок
   * пробуждения сам, когда вкладка уходит в фон (wake-lock.ts, пункт 2), а
   * очередной срок опроса мог быть назначен на 30 с вперёд — показать музыканту
   * протухший якорь на первом же взгляде значило бы соврать ему молча
   * (live-client.ts, `poll`).
   */
  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    wake.refresh();
    void live?.poll();
  };
  document.addEventListener('visibilitychange', onVisibility);

  host.appendChild(page);

  // Импульс заводится ТОЛЬКО когда есть за чем следить: страница без лайва
  // (волна B) не платит ни одним таймером.
  const beat = live ? (deps.pulse ?? defaultPulse)(() => { syncLive(); }) : null;
  // Первый вид кнопки — сразу, а не с первым импульсом: страницу открывают и
  // посреди служения, и состояние она обязана назвать на первом же взгляде.
  syncLive();

  return {
    setPayload(next: TextSharePayload): void {
      // Снимок открытой песни ДО подмены списка: по нему решается, та же она,
      // переехала, исчезла — и надо ли вообще трогать слой.
      const opened = reading >= 0 ? songs[reading] ?? null : null;
      const wasName = opened ? opened.name : null;
      // Сравнивается ВСЁ, что слой замораживает при монтаже: текст, связи и
      // расписание. Секции не косметика — по ним лист ведётся и по ним считается
      // конец песни, так что их правка обязана дойти до читателя.
      const wasSheet = opened
        ? JSON.stringify([opened.text, opened.links ?? null, opened.sections])
        : null;
      // Неоднозначность СТАРОГО списка не менее важна, чем нового: если «Grace»
      // там была дважды, то какую из них читал человек — уже не ответить, и
      // переезд на единственную оставшуюся был бы догадкой, а не опознанием.
      const wasAmbiguous = wasName !== null
        && songs.filter((x) => x.name === wasName).length > 1;

      songs = wordsSongs(next);
      title.textContent = wordsTitle(next);
      countEl.textContent = songs.length === 1
        ? t('textShare.songCountOne')
        : t('textShare.songCount', { n: songs.length });
      renderList();

      if (reading < 0) return;

      // ⚠️ ПЕСНЮ ОПОЗНАЁМ ПО ИМЕНИ, и это не лень: нагрузка НЕ несёт id песен
      // вовсе — они сняты ради приватности (share-text-payload.ts). Значит
      // единственное, чем читатель и страница различают «та же песня» после
      // перезаписи, — её имя.
      //
      // Ищем по ВСЕМУ новому списку, а не на прежней позиции: песню могли
      // переставить, и закрывать лист на перестановке не за что — читать ровно
      // то же самое.
      //
      // Совпадений больше одного — считаем, что не опознали. Имена в ссылке НЕ
      // уникальны (реприза, две аранжировки одной песни), и открыть однофамильца
      // значило бы молча подменить песню под читателем: тот самый отказ, против
      // которого написана вся ревизия, только для свободно читающего.
      const hits: number[] = [];
      if (wasName !== null && !wasAmbiguous) {
        for (let i = 0; i < songs.length; i++) if (songs[i].name === wasName) hits.push(i);
      }

      // ⚠️ ИМЕННО closeLayer, а не dropLayer. `openAt` накрыл страницу
      // (`slot.cover()` ставит inert + aria-hidden на всех детей body), а
      // отпускает её РОВНО одно место — этот вызов. Закрытие в обход него
      // оставляло страницу накрытой навсегда: список виден, ни один тап не
      // проходит, крестика нет (он был в снесённом слое), выход только
      // перезагрузкой — посреди служения. Найдено сквозным ревью 17.09.
      if (hits.length !== 1) { closeLayer(); return; }

      const at = hits[0];
      const sheet = JSON.stringify([songs[at].text, songs[at].links ?? null, songs[at].sections]);
      // Ревизия — счётчик ССЫЛКИ, а не песни: правка слов в пятой песне
      // поднимает её всем. Пересобирать слой на каждой такой правке значило бы
      // кидать читателя в начало листа столько раз, сколько ведущий нажал Save.
      if (at === reading && sheet === wasSheet) return;
      openAt(at);
    },
    unmount() {
      beat?.stop();
      document.removeEventListener('visibilitychange', onVisibility);
      // Замок отпускается вместе со страницей: держать экран телефона, на
      // котором больше ничего не открыто, незачем. Клиент лайва при этом НЕ
      // останавливается — им владеет тот, кто его создал (см. `live` в deps).
      wake.release();
      // Тема переживает страницу — она свойство человека (theme-boot.ts).
      dropLayer();
      badge?.destroy();
      // Слот стоит в body и переживёт хост страницы: `host.textContent = ''` до
      // него не достаёт. destroy заодно отпускает страницу (uncover).
      slot.destroy();
      watch.stop();
      dropNoindex();
      host.textContent = '';
    },
  };
}
